import Editor from '@monaco-editor/react';
import { editor } from 'monaco-editor';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  JobLogsContainer,
  JobLogsEditorContainer,
  JobLogsFilterContainer,
  JobLogsLoadingContainer,
} from '@/console/features/jobs/components/job-logs/job-logs.styled';
import { TJobLogsProps } from '@/console/features/jobs/components/job-logs/job-logs.types';
import { useJobLogs } from '@/console/features/jobs/hooks/use-job-logs/use-job-logs';
import { Alert } from '@/shared/components/ui/alert/alert';
import { Toggle } from '@/shared/components/ui/form/components/toggle/toggle';
import { LoadingDots } from '@/shared/components/ui/loading-dots/loading-dots';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { useCodeEditorSettings } from '@/shared/hooks/use-code-editor-settings/use-code-editor-settings';

export const JobLogs: React.FC<TJobLogsProps> = ({
  jobId,
  jobStatus,
  editorHeight,
}) => {
  const { t } = useTranslation();
  const { isJobRunning } = useJobStatus();
  const [shouldConvertTimestamp, setShouldConvertTimestamp] = useState(true);
  const { logs, logLines, isLoading } = useJobLogs(
    jobId,
    jobStatus,
    shouldConvertTimestamp,
  );
  const {
    currentThemeId,
    editorRef,
    handleBeforeMount,
    handleEditorMount,
    getCommonEditorOptions,
  } = useCodeEditorSettings<editor.IStandaloneCodeEditor>('log');

  const _isLoading = isLoading || isJobRunning(jobStatus);

  useEffect(() => {
    const model = editorRef.current?.getModel();

    if (!editorRef.current || !model) {
      return;
    }

    const lineCount = model.getLineCount();

    const decorations = logLines.flatMap((logLine, index) => {
      const className = logLine.colorClassName;
      const lineNumber = index + 1;

      if (!className || lineNumber > lineCount) {
        return [];
      }

      return [
        {
          range: {
            startLineNumber: lineNumber,
            startColumn: 1,
            endLineNumber: lineNumber,
            endColumn: model.getLineMaxColumn(lineNumber),
          },
          options: {
            inlineClassName: className,
            inlineClassNameAffectsLetterSpacing: true,
          },
        },
      ];
    });

    const decorationsCollection =
      editorRef.current.createDecorationsCollection(decorations);

    return () => decorationsCollection.clear();
  }, [editorRef, logLines]);

  if (!jobId || (!_isLoading && !logs?.length)) {
    return <Alert color="blue" text={t('pipelines:no_job_logs_info')} />;
  }

  return (
    <JobLogsContainer height={editorHeight ?? 'calc(100vh - 8.5rem)'}>
      <JobLogsFilterContainer>
        <Toggle
          checked={shouldConvertTimestamp}
          leftLabel={t('pipelines:utc_time')}
          rightLabel={t('pipelines:local_time')}
          onChange={(_, checked) => setShouldConvertTimestamp(checked)}
        />
      </JobLogsFilterContainer>
      <JobLogsEditorContainer>
        <Editor
          width="72rem"
          height="100%"
          language="log"
          theme={currentThemeId}
          value={logs}
          options={getCommonEditorOptions()}
          beforeMount={handleBeforeMount}
          onMount={handleEditorMount}
        />
        {isJobRunning(jobStatus) && (
          <JobLogsLoadingContainer>
            <LoadingDots />
          </JobLogsLoadingContainer>
        )}
      </JobLogsEditorContainer>
    </JobLogsContainer>
  );
};
